import { Match } from "model/Match/Match";
import {
  getSeasonRosterPointsOverride,
  SeasonPointsOverrides,
} from "model/Tournament/Segment/Season/Season";

export type NacSeasonRecord = {
  rosterId: string;
  points: number;
  matchPoints: (number | undefined)[];
  position: number | undefined;
};

export type NacSeasonRecordWithDisplay = {
  record: NacSeasonRecord;
  orgId: string | undefined;
  displayName: string | undefined;
};

const createNacSeasonRecord = (
  rosterId: string,
  numberOfPlaydays: number
): NacSeasonRecord => {
  return {
    rosterId,
    points: 0,
    matchPoints: Array(numberOfPlaydays).fill(undefined),
    position: undefined,
  };
};

export const calculateNacSeasonRecords = (
  seasonPointsOverrides: SeasonPointsOverrides | undefined,
  rosterIds: string[],
  matchesByPlayday: Match[][],
  numberOfPlaydays: number
) => {
  const records = new Map<string, NacSeasonRecord>();
  rosterIds.forEach((id) => {
    records.set(id, createNacSeasonRecord(id, numberOfPlaydays));
  });

  for (let playdayI = 0; playdayI < matchesByPlayday.length; playdayI++) {
    const matches = matchesByPlayday[playdayI];
    for (let matchI = 0; matchI < matches.length; matchI++) {
      const match = matches[matchI];
      if (!match.finalPositions) {
        continue;
      }

      for (let rosterI = 0; rosterI < rosterIds.length; rosterI++) {
        const rosterId = rosterIds[rosterI];
        const rosterRecord = records.get(rosterId);
        const finalPos = match.finalPositions[rosterId];
        if (!rosterRecord || !finalPos) {
          continue;
        }

        const overrideScore = !!seasonPointsOverrides
          ? getSeasonRosterPointsOverride(
              seasonPointsOverrides,
              match.id,
              rosterId
            )?.result
          : undefined;
        const nonQualOverrideScore =
          overrideScore === "Qual" ? 999999 : overrideScore;
        const points =
          Math.round(
            ((playdayI === 0 ? 0.3 * finalPos : 0.7 * finalPos) +
              Number.EPSILON) *
              100
          ) / 100;

        rosterRecord.matchPoints[playdayI] = nonQualOverrideScore ?? points;
        rosterRecord.points =
          Math.round(
            (rosterRecord.points +
              (nonQualOverrideScore ?? points) +
              Number.EPSILON) *
              100
          ) / 100;
      }
    }
  }

  const sortedRecords = Array.from(records.values()).sort(compareRecords);
  if (sortedRecords.length === 0) {
    return [];
  }

  sortedRecords[0].position = 1;
  for (let i = 1; i < sortedRecords.length; i++) {
    if (compareRecords(sortedRecords[i - 1], sortedRecords[i]) === 0) {
      sortedRecords[i].position = sortedRecords[i - 1].position;
    } else {
      sortedRecords[i].position = i + 1;
    }
  }

  return sortedRecords;
};

const compareRecords = (a: NacSeasonRecord, b: NacSeasonRecord): number => {
  return a.points - b.points;
};
